// Alby Penney - 2022
// React Mapbox Framework credit to Sunny Ng https://github.com/blahblahblah-/theweekendest

import React from 'react';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
// eslint-disable-next-line import/no-webpack-loader-syntax
import MapboxWorker from 'worker-loader!mapbox-gl/dist/mapbox-gl-csp-worker';
mapboxgl.workerClass = MapboxWorker;

mapboxgl.accessToken = process.env.REACT_APP_MAPBOX_TOKEN;
const mapboxStyle = "mapbox://styles/jjmelons/cl6pk0ila006k15pc5pzb14j2";
const center = [-117.1611, 32.7157]; // San Diego

const BACKEND_URL = "https://trolleytracker-sfaw.onrender.com";

// Set to false once a real MTS API key is configured in backend/.env
const TEST_MODE = false;

// Official MTS colors sourced from routes.txt route_color field
const LINE_COLORS = {
  "Blue":   "#0000FF",
  "Orange": "#FF6600",
  "Green":  "#009900",
  "Copper": "#B87333",
};
const DEFAULT_COLOR = "#555555";

// Pixel offset applied perpendicular to each line so overlapping routes sit adjacent
const LINE_OFFSETS = {
  "Blue":   -6,
  "Orange":  0,
  "Green":   6,
  "Copper":  12,
};

// Brighter versions for label legibility
const LABEL_COLORS = {
  "Blue":   "#5599FF",
  "Orange": "#FF8833",
  "Green":  "#33CC33",
  "Copper": "#D4994D",
};

// Direction candidates: label position relative to dot, Mapbox text-anchor, and unit vector
const DIRECTIONS = [
  { dir: 'top',          anchor: 'bottom',      dx:  0,    dy: -1    },
  { dir: 'bottom',       anchor: 'top',         dx:  0,    dy:  1    },
  { dir: 'right',        anchor: 'left',        dx:  1,    dy:  0    },
  { dir: 'left',         anchor: 'right',       dx: -1,    dy:  0    },
  { dir: 'top-right',    anchor: 'bottom-left', dx:  0.71, dy: -0.71 },
  { dir: 'top-left',     anchor: 'bottom-right',dx: -0.71, dy: -0.71 },
  { dir: 'bottom-right', anchor: 'top-left',    dx:  0.71, dy:  0.71 },
  { dir: 'bottom-left',  anchor: 'top-right',   dx: -0.71, dy:  0.71 },
];

const LABEL_OFFSET_PX = 18;

function ptSegDist(px, py, x1, y1, x2, y2) {
  const dx = x2 - x1, dy = y2 - y1;
  const lenSq = dx * dx + dy * dy;
  if (lenSq === 0) return Math.hypot(px - x1, py - y1);
  const t = Math.max(0, Math.min(1, ((px - x1) * dx + (py - y1) * dy) / lenSq));
  return Math.hypot(px - x1 - t * dx, py - y1 - t * dy);
}

// Legend rendered as an HTML overlay on top of the map
function Legend() {
  const lines = Object.entries(LINE_COLORS);
  return (
    <div style={{
      position: 'absolute', top: 10, right: 10, zIndex: 1,
      background: 'rgba(15,15,25,0.88)',
      borderRadius: 8,
      padding: '12px 14px',
      boxShadow: '0 2px 10px rgba(0,0,0,0.5)',
      minWidth: 160,
      fontFamily: '"Arial", sans-serif',
    }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: '#aaa', letterSpacing: 1, marginBottom: 10, textTransform: 'uppercase' }}>
        SD MTS Trolley
      </div>

      {lines.map(([name, color]) => (
        <div key={name} style={{ display: 'flex', alignItems: 'center', marginBottom: 7 }}>
          <div style={{
            width: 28, height: 5, borderRadius: 3,
            background: color, marginRight: 9, flexShrink: 0,
          }} />
          <span style={{ fontSize: 12, color: '#eee' }}>{name} Line</span>
        </div>
      ))}

      <div style={{ borderTop: '1px solid rgba(255,255,255,0.12)', margin: '10px 0 8px' }} />

      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 6 }}>
        <div style={{
          width: 10, height: 10, borderRadius: '50%',
          background: '#fff', border: '1.5px solid #000',
          marginRight: 9, flexShrink: 0,
        }} />
        <span style={{ fontSize: 11, color: '#bbb' }}>Station</span>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 6 }}>
        <div style={{
          width: 13, height: 13, borderRadius: '50%',
          background: '#fff', border: '2.5px solid #000',
          marginRight: 9, flexShrink: 0,
        }} />
        <span style={{ fontSize: 11, color: '#bbb' }}>Transfer Station</span>
      </div>
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <div style={{
          width: 12, height: 12, borderRadius: '50%',
          background: '#FF6600', border: '2px solid #fff',
          marginRight: 9, flexShrink: 0,
        }} />
        <span style={{ fontSize: 11, color: '#bbb' }}>Vehicle</span>
      </div>
    </div>
  );
}

class Map extends React.Component {
  constructor(props) {
    super(props);
    this.state = { loading: true, tooltip: null };
    this.mapLoaded = false;
    this.currentVehicles = [];
    this.lastFetchTime = null;
  }

  componentDidMount() {
    this.map = new mapboxgl.Map({
      container: this.mapContainer,
      style: mapboxStyle,
      center: center,
      minZoom: 9,
      zoom: 14,
      maxPitch: 0,
    });

    this.map.addControl(new mapboxgl.NavigationControl({ showCompass: true }), "bottom-right");

    this.map.on('load', () => {
      this.mapLoaded = true;

      // Vehicle source — on top of everything; layers added last so they render above stops/routes
      this.map.addSource('vehicles', {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: [] },
      });
      // If vehicle is trolley, should be normal sized. 
      // If vehicle is not trolley, should be smaller.
      this.map.addLayer({
        id: 'vehicle-circles',
        type: 'circle',
        source: 'vehicles',
        paint: {
          'circle-radius': ['case', ['get', 'is_trolley'], 7, 3],
          'circle-color': ['get', 'color'],
          'circle-stroke-width': 2.5,
          'circle-stroke-color': '#ffffff',
        },
      });
      // Small directional arrow on trolley dots — triangle offset from center,
      // rotated to bearing so it sits on the white stroke ring pointing toward travel direction
      const arrowSize = 32;
      const arrowCanvas = document.createElement('canvas');
      arrowCanvas.width = arrowSize;
      arrowCanvas.height = arrowSize;
      const actx = arrowCanvas.getContext('2d');
      const cx = arrowSize / 2, cy = arrowSize / 2;
      actx.beginPath();
      actx.moveTo(cx, cy - 13);      // tip (points north before rotation)
      actx.lineTo(cx - 3.5, cy - 8); // base left
      actx.lineTo(cx + 3.5, cy - 8); // base right
      actx.closePath();
      actx.fillStyle = '#ffffff';
      actx.fill();
      actx.strokeStyle = '#ffffff';
      actx.lineWidth = 1;
      actx.stroke();
      const arrowPixels = actx.getImageData(0, 0, arrowSize, arrowSize);
      this.map.addImage('vehicle-arrow', { width: arrowSize, height: arrowSize, data: arrowPixels.data });

      this.map.addLayer({
        id: 'vehicle-arrows',
        type: 'symbol',
        source: 'vehicles',
        filter: ['==', ['get', 'is_trolley'], true],
        layout: {
          'icon-image': 'vehicle-arrow',
          'icon-size': 1,
          'icon-rotate': ['get', 'bearing'],
          'icon-rotation-alignment': 'map',
          'icon-allow-overlap': true,
          'icon-ignore-placement': true,
        },
      });

      this.map.addLayer({
        id: 'vehicle-labels',
        type: 'symbol',
        source: 'vehicles',
        layout: {
          'text-field': ['get', 'label'],
          'text-size': 11,
          'text-font': ['Open Sans Semibold', 'Arial Unicode MS Bold'],
          'text-optional': true,
          'text-anchor': ['match', ['get', 'textDirection'],
            'top',          'bottom',
            'bottom',       'top',
            'right',        'left',
            'left',         'right',
            'top-right',    'bottom-left',
            'top-left',     'bottom-right',
            'bottom-right', 'top-left',
            'bottom-left',  'top-right',
            'top',
          ],
          'text-offset': ['match', ['get', 'textDirection'],
            'top',          ['literal', [ 0,    -0.9]],
            'bottom',       ['literal', [ 0,     0.9]],
            'right',        ['literal', [ 0.9,   0  ]],
            'left',         ['literal', [-0.9,   0  ]],
            'top-right',    ['literal', [ 0.65, -0.65]],
            'top-left',     ['literal', [-0.65, -0.65]],
            'bottom-right', ['literal', [ 0.65,  0.65]],
            'bottom-left',  ['literal', [-0.65,  0.65]],
            ['literal', [0, 0.9]],
          ],
        },
        paint: {
          'text-color': ['get', 'labelColor'],
        },
      });

      // Fetch routes and stops — both inserted BEFORE vehicle layers
      this.loadRoutes();
      this.loadStops();

      this.fetchData();
      this.pollInterval = setInterval(() => this.fetchData(), 30000);
      this.map.on('moveend', () => this.updateLabelPositions());
      this.map.on('movestart', () => this.setState({ tooltip: null }));

      this.map.on('mouseenter', 'vehicle-circles', (e) => {
        this.map.getCanvas().style.cursor = 'pointer';
        const p = e.features[0].properties;
        const secsAgo = this.lastFetchTime
          ? Math.round((Date.now() - this.lastFetchTime) / 1000)
          : 0;
        this.setState({
          tooltip: {
            x: e.point.x,
            y: e.point.y,
            label: p.label,
            headsign: p.trip_headsign || (p.route_name + ' Line'),
            routeName: p.route_name,
            color: p.color,
            secsAgo,
          },
        });
      });

      this.map.on('mouseleave', 'vehicle-circles', () => {
        this.map.getCanvas().style.cursor = '';
        this.setState({ tooltip: null });
      });
    });
  }

  componentWillUnmount() {
    clearInterval(this.pollInterval);
    this.map.remove();
  }

  loadRoutes() {
    fetch(`${BACKEND_URL}/routes`)
      .then(res => res.json())
      .then(geojson => {
        this.map.addSource('trolley-routes', { type: 'geojson', data: geojson });
        // Insert route layers below vehicle-circles
        const routeOffset = ['match', ['get', 'route_name'],
          ...Object.entries(LINE_OFFSETS).flat(),
          0,
        ];
        this.map.addLayer({
          id: 'trolley-routes-casing',
          type: 'line',
          source: 'trolley-routes',
          layout: { 'line-join': 'round', 'line-cap': 'round' },
          paint: { 'line-color': '#ffffff', 'line-width': 7, 'line-opacity': 0.5, 'line-offset': routeOffset },
        }, 'vehicle-circles');
        this.map.addLayer({
          id: 'trolley-routes-line',
          type: 'line',
          source: 'trolley-routes',
          layout: { 'line-join': 'round', 'line-cap': 'round' },
          paint: { 'line-color': ['get', 'color'], 'line-width': 4, 'line-opacity': 0.95, 'line-offset': routeOffset },
        }, 'vehicle-circles');
      })
      .catch(err => console.error("Failed to load routes:", err));
  }

  loadStops() {
    fetch(`${BACKEND_URL}/stops`)
      .then(res => res.json())
      .then(geojson => {
        this.map.addSource('stops', { type: 'geojson', data: geojson });

        // Stop dots — white with black border; transfer stations larger
        // Inserted below vehicle-circles so vehicles always render on top
        this.map.addLayer({
          id: 'stop-circles',
          type: 'circle',
          source: 'stops',
          paint: {
            'circle-radius': ['case', ['get', 'transfer'], 6, 4],
            'circle-color': '#ffffff',
            'circle-stroke-width': ['case', ['get', 'transfer'], 2.5, 1.5],
            'circle-stroke-color': '#000000',
          },
        }, 'vehicle-circles');

        // Stop name labels — white text, only visible when zoomed in enough
        this.map.addLayer({
          id: 'stop-labels',
          type: 'symbol',
          source: 'stops',
          minzoom: 13,
          layout: {
            'text-field': ['get', 'name'],
            'text-size': ['case', ['get', 'transfer'], 11, 10],
            'text-font': ['case',
              ['get', 'transfer'],
              ['literal', ['Open Sans Semibold', 'Arial Unicode MS Bold']],
              ['literal', ['Open Sans Regular', 'Arial Unicode MS Regular']],
            ],
            'text-variable-anchor': ['right', 'left', 'top', 'bottom', 'top-right', 'top-left', 'bottom-right', 'bottom-left'],
            'text-radial-offset': 0.6,
            'text-justify': 'auto',
            'text-optional': true,
            'text-max-width': 8,
          },
          paint: {
            'text-color': '#ffffff',
            'text-halo-color': 'rgba(0,0,0,0.65)',
            'text-halo-width': 1,
          },
        }, 'vehicle-circles');
      })
      .catch(err => console.error("Failed to load stops:", err));
  }

  fetchData() {
    const url = TEST_MODE ? `${BACKEND_URL}/vehicles?test=true` : `${BACKEND_URL}/vehicles`;
    fetch(url)
      .then(res => res.json())
      .then(data => {
        this.currentVehicles = data.vehicles;
        this.lastFetchTime = Date.now();
        this.updateLabelPositions();
        this.setState({ loading: false });
      })
      .catch(err => console.error("Failed to fetch vehicle positions:", err));
  }

  updateLabelPositions() {
    if (!this.currentVehicles.length || !this.mapLoaded) return;

    // Collect visible route segments in screen space
    const segments = [];
    for (const f of this.map.queryRenderedFeatures({ layers: ['trolley-routes-line'] })) {
      const rings = f.geometry.type === 'LineString'
        ? [f.geometry.coordinates]
        : f.geometry.coordinates;
      for (const ring of rings) {
        for (let i = 0; i < ring.length - 1; i++) {
          const p1 = this.map.project(ring[i]);
          const p2 = this.map.project(ring[i + 1]);
          segments.push([p1.x, p1.y, p2.x, p2.y]);
        }
      }
    }

    const features = this.currentVehicles.map(v => {
      const pt = this.map.project([v.lon, v.lat]);

      let bestDir = DIRECTIONS[0];
      let bestDist = -1;
      for (const d of DIRECTIONS) {
        const lx = pt.x + d.dx * LABEL_OFFSET_PX;
        const ly = pt.y + d.dy * LABEL_OFFSET_PX;
        let minDist = Infinity;
        for (const [x1, y1, x2, y2] of segments) {
          const dist = ptSegDist(lx, ly, x1, y1, x2, y2);
          if (dist < minDist) minDist = dist;
        }
        if (minDist > bestDist) { bestDist = minDist; bestDir = d; }
      }

      return {
        type: 'Feature',
        geometry: { type: 'Point', coordinates: [v.lon, v.lat] },
        properties: {
          color:         LINE_COLORS[v.route_name]  || DEFAULT_COLOR,
          labelColor:    LABEL_COLORS[v.route_name] || DEFAULT_COLOR,
          label:         v.label,
          route_name:    v.route_name || '',
          trip_headsign: v.trip_headsign || '',
          bearing:       v.bearing ?? 0,
          is_trolley:    !!v.is_trolley,
          textDirection: bestDir.dir,
        },
      };
    });

    this.map.getSource('vehicles').setData({ type: 'FeatureCollection', features });
  }

  render() {
    const mapStyle = { position: 'absolute', top: 0, bottom: 0, width: '100%' };
    const { tooltip: tt } = this.state;
    return (
      <>
        <div style={mapStyle} ref={el => this.mapContainer = el} />
        <Legend />
        {tt && (
          <div style={{
            position: 'absolute',
            left: tt.x,
            top: tt.y,
            transform: 'translate(-50%, calc(-100% - 14px))',
            pointerEvents: 'none',
            zIndex: 10,
            fontFamily: 'Arial, sans-serif',
            filter: 'drop-shadow(0 3px 10px rgba(0,0,0,0.5))',
          }}>
            <div style={{
              background: tt.color, color: '#fff',
              padding: '9px 14px', fontSize: 15,
              borderRadius: '8px 8px 0 0', whiteSpace: 'nowrap',
            }}>
              <b>{tt.label}</b>{' '}{tt.headsign}
            </div>
            <div style={{
              background: '#fff', color: '#1a1a1a',
              padding: '9px 14px 11px', fontSize: 13,
              lineHeight: 1.75, borderRadius: '0 0 8px 8px',
            }}>
              <div>{tt.routeName} Line</div>
              <div style={{ color: '#555', fontStyle: 'italic' }}>{tt.secsAgo} sec ago</div>
            </div>
            <div style={{
              position: 'absolute', bottom: -8, left: '50%',
              transform: 'translateX(-50%)',
              width: 0, height: 0,
              borderLeft: '8px solid transparent',
              borderRight: '8px solid transparent',
              borderTop: '8px solid #fff',
            }} />
          </div>
        )}
      </>
    );
  }
}

export default Map;
